import React from 'react';
import { useParams } from 'react-router-dom';

import { useSearchParams } from 'Hooks/useSearchParams';
import useStore from 'Hooks/useStore';
import alertToast from 'Util/ToastifyUtils';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { useUserDetail } from '../Hooks/useUserDetail';
import CustomerAccountInner from './CustomerAccountInner';

/**
 * Fetch wrapper for the customer's own account page. As with the staff page, the Inner is keyed
 * on dataUpdatedAt so every save/cancel remounts it onto fresh server data — that remount is
 * what resets the edit session. Supports ?mode=edit for flows that deep-link straight into
 * editing.
 */
export default function CustomerAccountPage() {
	const { tab } = useParams<{ tab: string }>();
	const { getSearchParam } = useSearchParams();
	const store = useStore();

	const userId = store.userId as string;
	const { data: user, isPending, isError, refetch, dataUpdatedAt } = useUserDetail(userId);

	if (isError) {
		alertToast('Could not fetch user.', 'error');
		return <LottieSpinner />;
	}
	if (isPending) {
		return <LottieSpinner />;
	}
	if (isNullOrUndefined(user)) {
		alertToast('Could not fetch user', 'error');
		return <LottieSpinner />;
	}

	const defaultMode = getSearchParam('mode') === 'edit' ? EntityFormMode.EDIT : undefined;

	return (
		<CustomerAccountInner
			key={`${user.id}-${dataUpdatedAt}`}
			user={user}
			refetchUser={refetch}
			initialTab={tab ?? 'account'}
			defaultMode={defaultMode}
		/>
	);
}
